import { JsonSocket } from '@amatiasq/socket';
import { patch } from '@amatiasq/state-delta';
import type { Json } from '@amatiasq/state-delta';

import { Vector } from '../shared/vector.ts';

import type { EntityId, PlayerInput } from '../shared/entities.ts';
import { createLink } from '../shared/link.ts';
import type {
  ClientMessage,
  PlayerAction,
  Score,
  ServerMessage,
} from '../shared/protocol.ts';
import { applyDelta } from '../shared/snapshot.ts';
import type { WorldChanges, WorldSnapshot } from '../shared/snapshot.ts';
import { getPlayer } from '../shared/world.ts';
import type { World } from '../shared/world.ts';
import type { createPrediction } from './prediction.ts';

const PING_INTERVAL = 2000;

/** How often the fake wire is asked whether anything has landed. */
const FAKE_WIRE_TICK = 8;

/**
 * The game as seen from here: a socket that comes back on its own, plus the
 * mirror of what the server has told us. Deltas only make sense against that
 * mirror, so a reconnection throws it away — the server's copy is gone too.
 */
export function connect(
  url: string,
  world: World,
  prediction: ReturnType<typeof createPrediction>,
  name: string,
  events: {
    onDeath: () => void;
    onScores: (scores: Score[]) => void;
    onChanges: (changes: WorldChanges, isArrival: boolean) => void;
  },
) {
  const socket = new JsonSocket<ServerMessage, ClientMessage>(url);
  const wire = fakeWire(message => socket.send(message), receive);

  let known: Record<string, Json> = {};
  let playerId: EntityId | undefined;
  let latency = 0;
  /**
   * The first state after a join builds the arena as it already was. Everything
   * in it is created at once, and none of it just happened.
   */
  let isArrival = true;

  socket.onOpen(join);
  socket.onReconnect(join);
  socket.onMessage(wire.receive);
  setInterval(sendPing, PING_INTERVAL);

  return {
    join,
    sendInput: (input: Partial<PlayerInput>) =>
      wire.send({ type: 'input', data: input, at: prediction.stamp() }),
    sendAction: (action: PlayerAction) =>
      wire.send({ type: 'action', data: action, at: prediction.stamp() }),
    playerId: () => playerId,
    latency: () => latency,
  };

  function join() {
    known = {};
    playerId = undefined;
    isArrival = true;
    prediction.join();
    world.entities.clear();
    wire.send({ type: 'join', data: { name } });
  }

  function receive(message: ServerMessage) {
    if (message.type === 'welcome') {
      playerId = message.data.playerId;
      return;
    }

    if (message.type === 'state') {
      known = patch(known, message.data);
      correctOurPlayer(message.applied);

      const changes = applyDelta(
        world,
        known as unknown as WorldSnapshot,
        message.data,
        playerId,
      );

      events.onChanges(changes, isArrival);
      isArrival = false;
      return;
    }

    if (message.type === 'pong') {
      latency = Math.round(performance.now() - message.data);
      return;
    }

    if (message.type === 'scores') {
      events.onScores(message.data);
      return;
    }

    if (message.type === 'dead') {
      playerId = undefined;
      events.onDeath();
    }
  }

  /**
   * Our own player, before the rest of the snapshot is believed: back to where
   * the server put it, then forward again through every frame this end played
   * that the server had not heard of. Whatever gap is left afterwards is a real
   * lead, which is what the bullets leaving our barrel are corrected by.
   */
  function correctOurPlayer(applied: number) {
    const snapshot = known as unknown as WorldSnapshot;
    const mine = playerId ? getPlayer(world, playerId) : undefined;
    const said = playerId ? snapshot[playerId] : undefined;

    if (!mine || !said) return;

    prediction.reconcile(mine, Vector.of(said.x, said.y), applied);
  }

  function sendPing() {
    wire.send({ type: 'ping', data: performance.now() });
  }
}

/**
 * A wire that takes its time, because the local one does not take any: 0 ms is
 * the one condition the prediction and the smoothing were built for and never
 * meet in production. `?lag=150` puts a 150 ms round trip back on, half each
 * way, and `?jitter=40` makes it wobble. Without either, this is the socket
 * itself and costs nothing.
 */
function fakeWire(
  send: (message: ClientMessage) => void,
  receive: (message: ServerMessage) => void,
) {
  const asked = new URLSearchParams(location.search);
  const lag = Number(asked.get('lag')) || 0;
  const jitter = Number(asked.get('jitter')) || 0;

  if (lag <= 0 && jitter <= 0) return { send, receive };

  const latency = lag / 2;
  const out = createLink<ClientMessage>({ latency, jitter, seed: 2 });
  const back = createLink<ServerMessage>({ latency, jitter, seed: 9 });

  setInterval(() => {
    for (const message of out.receive(performance.now())) send(message);
    for (const message of back.receive(performance.now())) receive(message);
  }, FAKE_WIRE_TICK);

  return {
    send: (message: ClientMessage) => out.send(message, performance.now()),
    receive: (message: ServerMessage) => back.send(message, performance.now()),
  };
}
